import { Link, useLocation } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import IconExpandLess from '@mui/icons-material/ExpandLess'
import IconExpandMore from '@mui/icons-material/ExpandMore'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'

import { RouterListItemButton } from '../links/routerLinks'

export type NewDrawerItem = {
    primary: string
    secondary?: string
    link?: string
    icon?: any
    children?: NewDrawerItem[]
    isActive?: boolean
    color?: string
    fontColor?: string
    defaultOpen?: boolean
}

export function NewDrawerListItem(props: Readonly<NewDrawerItem & { activeContrast?: boolean }>) {
    const {
        icon,
        primary,
        secondary,
        link,
        children,
        color,
        fontColor = 'inherit',
        defaultOpen,
        activeContrast = false,
    } = props
    const [open, setOpen] = useState(defaultOpen)
    const { pathname } = useLocation()
    const ref = useRef<HTMLDivElement>(null)
    const isActive = pathname === link

    const shouldBeOpen = useMemo(() => {
        if (!children || children.length === 0) return false
        return children.some((child) => pathname === child.link)
    }, [children, pathname])

    useEffect(() => {
        if (shouldBeOpen) {
            setOpen(true)
        }
    }, [shouldBeOpen])

    useEffect(() => {
        if (isActive) {
            ref.current?.scrollIntoView({ behavior: 'instant', block: 'center' })
        }
    }, [isActive])

    const handleToggle = () => {
        if (children?.length) {
            setOpen((prev) => !prev)
        }
    }

    return (
        <>
            <ListItemButton
                ref={ref}
                component={link ? Link : 'div'}
                to={link || undefined}
                onClick={children ? handleToggle : undefined}
                sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    '&.active': {
                        backgroundColor: color ?? 'action.selected',
                        color: fontColor,
                        '& .MuiListItemText-secondary': {
                            color: fontColor,
                        },
                        ...(activeContrast && {
                            '& .MuiSvgIcon-root': {
                                color: fontColor,
                            },
                        }),
                    },
                }}
            >
                {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
                <ListItemText primary={primary} secondary={secondary} />
                {children &&
                    (open ? (
                        <IconExpandLess color="primary" />
                    ) : (
                        <IconExpandMore color="primary" />
                    ))}
            </ListItemButton>
            {children ? (
                <Collapse in={open} timeout="auto" mountOnEnter unmountOnExit>
                    <List disablePadding dense>
                        {children
                            .filter((child) => child.link)
                            .map((child) => (
                                <RouterListItemButton
                                    key={`${child.link}-${child.primary}`}
                                    nativeButton={false}
                                    to={child.link!}
                                    sx={{
                                        pl: 4,
                                        color: 'inherit',
                                        textDecoration: 'none',
                                        '&.active': {
                                            backgroundColor: color ?? 'action.selected',
                                            ...(activeContrast && {
                                                color: fontColor,
                                                '& .MuiListItemText-secondary': {
                                                    color: fontColor,
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: fontColor,
                                                },
                                            }),
                                        },
                                    }}
                                >
                                    {child.icon ? <ListItemIcon>{child.icon}</ListItemIcon> : null}
                                    <ListItemText
                                        primary={child.primary}
                                        secondary={child.secondary}
                                        slotProps={{
                                            primary: { variant: 'body2' },
                                        }}
                                    />
                                </RouterListItemButton>
                            ))}
                    </List>
                </Collapse>
            ) : null}
        </>
    )
}
